import { ManagedContentBanner } from '@seek/cms-ui';
import {
  HirerContractBalance,
  type TrackingEvent,
} from '@seek/hirer-ecommerce';
import type { Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Stack, Columns, Column, PageBlock } from 'braid-design-system';
import { Fragment, useState } from 'react';

import { ClaimCompanyNudge } from 'src/App/Components/ClaimCompanyNudge/ClaimCompanyNudge';
import DancingDots from 'src/App/Components/DancingDots';
import FindRecruiterDialog from 'src/App/Components/FindRecruiterDialog/FindRecruiterDialog';
import { useFindRecruiterDialogTracking } from 'src/App/Components/FindRecruiterDialog/useFindRecruiterDialogTracking';
import { Hero } from 'src/App/Components/Hero/Hero';
import HiringAdvice from 'src/App/Components/HiringAdvice/HiringAdvice';
import { PromoBanner } from 'src/App/Components/PromoBanner/PromoBanner';
import { CompanyProfileBannerImageSvg } from 'src/App/Components/PromoBanner/SVGs/CompanyProfileBannerImageSvg';
import { PromoCodesBanner } from 'src/App/Components/PromoCodesBanner/PromoCodesBanner';
import RecentJobs from 'src/App/Components/RecentJobs/RecentJobs';
import TalentSearch from 'src/App/Components/TalentSearch/TalentSearch';
import { VerifyEmailAlert } from 'src/App/Components/VerifyEmail/VerifyEmailAlert';
import { analyticsEvents } from 'src/constants/analytics';
import { TRACKING_DASHBOARD_MAIN } from 'src/constants/tracking';
import { useIsDashboardDataLoaded } from 'src/hooks/use-is-dashboard-data-loaded';
import {
  PageLoadTracker,
  trackPageInteraction,
} from 'src/modules/AnalyticsTracker';
import { useAccountContext, useDashboardJobs } from 'src/modules/ApiClient';
import { IntercomConfiguration } from 'src/modules/Intercom/IntercomConfiguration';
import type { EmployerSiteName, Environment } from 'src/types';
import { isAutomationTest } from 'src/utils/automation';
import {
  getCountryFromSite,
  getLanguage,
  getLocale,
  isStaging,
} from 'src/utils/config';
import { getCompanyProfileEditUrl } from 'src/utils/getUrls';
import { isEmployerANZSite } from 'src/utils/isEmployerANZSite';

import translations from './.vocab';

export interface Props {
  site: EmployerSiteName;
  environment: Environment;
  locale: Locale;
  activeTab?: string;
  activeSubTab?: string;
  renderBasicHeader?: boolean;
  children?: any;
}

const Dashboard = ({ site, locale, environment }: Props) => {
  const { data: accountContext } = useAccountContext();
  const { data: jobsInfo } = useDashboardJobs();
  const dashboardDataLoaded = useIsDashboardDataLoaded();
  const { t } = useTranslations(translations);
  const trackingData = useFindRecruiterDialogTracking();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  if (!accountContext || !dashboardDataLoaded) {
    return <DancingDots />;
  }

  const trackAdBalance = (event: TrackingEvent) => {
    const { eventName, data } = event;
    const trackData = {
      ...data,
    };
    trackPageInteraction(eventName, undefined, trackData);
  };

  const hirerECommerceLocale = getLocale();
  const country = getCountryFromSite(site);

  const enabledAnzSpecificContent = isEmployerANZSite(site);

  const enabledClaimCompanyProfileBanner =
    accountContext?.currentAdvertiser.permissionOptions.canClaimCompanyProfile;
  const enabledCompanyProfileBanner =
    accountContext?.currentAdvertiser.permissionOptions.canManageCompanyProfile;

  const enabledCreateAJobButton =
    accountContext?.currentAdvertiser.permissionOptions.canCreateJobs;

  const enabledRecentJobs =
    jobsInfo?.dashboardJobs.__typename === 'DashboardJobsResponseSuccess' &&
    jobsInfo.dashboardJobs.jobs.edges.length > 0;

  const showVerifyEmailPrompt =
    Boolean(accountContext?.emailVerified) === false;

  const closeDialog = () => {
    trackPageInteraction(
      analyticsEvents.TRACK_DIALOG_DISMISSED,
      undefined,
      trackingData,
    );
    setIsDialogOpen(false);
  };

  return (
    <Fragment>
      {!isAutomationTest() && (
        <>
          <PageLoadTracker
            accountContext={accountContext}
            site={site}
            locale={locale}
          />
          <IntercomConfiguration
            accountContext={accountContext}
            site={site}
            locale={locale}
          />
        </>
      )}
      {dashboardDataLoaded ? (
        <>
          <Stack space="xxlarge">
            <Stack space="none">
              {/* Site Banners */}
              {!showVerifyEmailPrompt && (
                <ManagedContentBanner
                  platform="employer"
                  context="hirer_dashboard"
                  environment={environment}
                  locale={locale}
                />
              )}
              <PromoCodesBanner />
              {showVerifyEmailPrompt && (
                <VerifyEmailAlert
                  email={accountContext?.identityContext?.email}
                />
              )}

              <Hero />
            </Stack>

            <PageBlock width="large">
              <Stack space="xxlarge">
                {enabledClaimCompanyProfileBanner && <ClaimCompanyNudge />}

                {enabledRecentJobs && (
                  <RecentJobs showDialog={setIsDialogOpen} locale={locale} />
                )}

                <Stack space={{ mobile: 'none', tablet: 'large' }}>
                  {/* Talent Search & Contract Balance tiles */}
                  <Columns
                    space={{ mobile: 'small', tablet: 'large' }}
                    collapseBelow="desktop"
                    alignY="top"
                  >
                    <Column>
                      <TalentSearch />
                    </Column>
                    <Column>
                      <HirerContractBalance
                        site={site}
                        language={getLanguage()}
                        isStaging={isStaging()}
                        locale={hirerECommerceLocale}
                        advertiserNation={country}
                        tracking={trackAdBalance}
                        canCreateJobs={enabledCreateAJobButton}
                      />
                    </Column>
                  </Columns>
                  {enabledCompanyProfileBanner && (
                    <PromoBanner
                      heading={t(
                        'Showcase what makes your company a great place to work',
                      )}
                      description={t(
                        "Update your company profile and stand out to candidates – it's easy.",
                      )}
                      image={<CompanyProfileBannerImageSvg />}
                      buttonText={t('Manage your profile')}
                      buttonUrl={getCompanyProfileEditUrl()}
                      tracking={{
                        eventName:
                          analyticsEvents.TRACK_COMPANY_PROFILE_BANNER_LINK,
                        actionOrigin: TRACKING_DASHBOARD_MAIN,
                        elementText: 'Manage your profile',
                      }}
                    />
                  )}
                </Stack>

                {enabledAnzSpecificContent && <HiringAdvice />}
              </Stack>
            </PageBlock>
          </Stack>
          {isDialogOpen && (
            <FindRecruiterDialog onClose={closeDialog} locale={locale} />
          )}
        </>
      ) : (
        <DancingDots />
      )}
    </Fragment>
  );
};

export default Dashboard;
